import type { ErroApi } from '@malgoos/shared';
import { ErroRequisicao, segundosDeEspera } from '../../web/src/nucleo/api';
import { baseDaApi, cofre, ehNativo } from './nativo';

/**
 * Portal do Cliente (Onda 1): cliente HTTP do app. Só fala com `/api/portal/v1` (o host do portal não alcança mais nada).
 * Navegador: cookie HttpOnly da mesma origem (`credentials: 'same-origin'`). App nativo: Bearer `mp_...` do armazenamento
 * seguro, sem cookie (`credentials: 'omit'`), com o cabeçalho que pede o token no corpo do login. Os erros saem como o
 * `ErroRequisicao` do MalgoOS, para a mesma tradução por código (`mensagemDeErro`).
 */
type Metodo = 'GET' | 'POST' | 'PATCH' | 'DELETE';

let aoExpirar: (() => void) | null = null;
export function definirAoExpirar(fn: () => void) {
  aoExpirar = fn;
}

export const urlDaApi = (caminho: string) => `${baseDaApi()}/api/portal/v1${caminho}`;

async function cabecalhos(corpo: unknown): Promise<Record<string, string>> {
  const h: Record<string, string> = { Accept: 'application/json' };
  if (corpo !== undefined && !(corpo instanceof FormData)) h['Content-Type'] = 'application/json';
  if (ehNativo()) {
    h['X-Portal-Cliente'] = 'nativo';
    const token = await cofre.lerToken();
    if (token) h.Authorization = `Bearer ${token}`;
  }
  return h;
}

export async function chamar<T = unknown>(metodo: Metodo, caminho: string, corpo?: unknown, params?: Record<string, unknown>): Promise<T> {
  const url = new URL(urlDaApi(caminho), window.location.origin);
  for (const [k, v] of Object.entries(params ?? {})) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  const r = await fetch(url.toString(), {
    method: metodo,
    credentials: ehNativo() ? 'omit' : 'same-origin',
    headers: await cabecalhos(corpo),
    body: corpo === undefined ? undefined : corpo instanceof FormData ? corpo : JSON.stringify(corpo),
  });
  if (r.status === 204) return undefined as T;
  const texto = await r.text();
  let json: unknown;
  try {
    json = texto ? JSON.parse(texto) : undefined;
  } catch {
    json = undefined;
  }
  if (!r.ok) {
    if (r.status === 401 && !caminho.startsWith('/acesso/')) aoExpirar?.();
    throw new ErroRequisicao(r.status, (json as ErroApi | undefined) ?? { code: r.status >= 500 ? 'servidor.indisponivel' : 'erro', message: '' }, segundosDeEspera(r.headers.get('Retry-After'), json));
  }
  return json as T;
}

export const pget = <T>(caminho: string, params?: Record<string, unknown>) => chamar<T>('GET', caminho, undefined, params);
export const ppost = <T>(caminho: string, corpo: unknown = {}) => chamar<T>('POST', caminho, corpo);
export const ppatch = <T>(caminho: string, corpo: unknown = {}) => chamar<T>('PATCH', caminho, corpo);

/**
 * Baixa um arquivo do portal (documento, exportação). No navegador o cookie vai sozinho e o arquivo é salvo pelo
 * navegador; no app nativo o Bearer vai no cabeçalho e o arquivo abre pelo compartilhamento do sistema, quando existe.
 */
export async function baixar(caminho: string, nomePadrao: string) {
  const r = await fetch(new URL(urlDaApi(caminho), window.location.origin).toString(), { credentials: ehNativo() ? 'omit' : 'same-origin', headers: await cabecalhos(undefined) });
  if (!r.ok) {
    let json: unknown;
    try {
      json = await r.json();
    } catch {
      json = undefined;
    }
    if (r.status === 401) aoExpirar?.();
    throw new ErroRequisicao(r.status, (json as ErroApi | undefined) ?? { code: 'erro', message: '' });
  }
  const nome = /filename\*=UTF-8''([^;]+)/.exec(r.headers.get('content-disposition') ?? '')?.[1];
  const blob = await r.blob();
  const arquivo = nome ? decodeURIComponent(nome) : nomePadrao;
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (ehNativo() && nav.canShare?.({ files: [new File([blob], arquivo, { type: blob.type })] })) {
    await nav.share({ files: [new File([blob], arquivo, { type: blob.type })] }).catch(() => undefined);
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = arquivo;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
